import { TableCell } from '@/components/Datatable/TableCell';
import { IDatatableData } from '@/type/listing.types';
import { FieldValues } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { AddressDisplay } from '@/components/AddressDisplay';
import { Checkbox } from '@/components/Controls/Checkbox';
import { TableHeaderActions } from '@/components/Custom/Datatable/TableHeaderActions';
import { useSelectedTableItems } from '@/hooks/selected-table-items.hooks';
import { TableMenu } from '@/components/Menu/TableMenu';
import { SubMenuItem } from '@/components/Menu/SubMenuItem';
import { CellContentWithRedirect } from '@/components/Custom/Datatable/CellContentWithRedirect';
import { StatusTag } from '@/components/Tag/StatusTag';
import { useUserRole } from '@/hooks/auth.hooks';
import { EditGroupTab } from '@/type/ui/tab/groupTab';

export const GroupTableData = <TSchema extends FieldValues>({
  t,
  data,
  pendingDeactivate,
  onDeactivate,
}: IDatatableData<TSchema>) => {
  const navigate = useNavigate();

  const { isAdmin, isModerator } = useUserRole();

  const {
    numberOfItemsText,
    selectedTableData,
    onSelectedItem,
    handleDeactivate,
  } = useSelectedTableItems({
    t,
    data,
    onDeactivate,
  });

  if (!data || data.length === 0) {
    return <></>;
  }

  return (
    <>
      {selectedTableData?.length > 0 && (
        <TableHeaderActions
          colSpan={8}
          text={numberOfItemsText}
          disableDeactivate={selectedTableData?.every((item) => !item?.active)}
          disableDownload
          onDeactivate={handleDeactivate}
          onDownload={() => {}}
          onCreate={() => navigate('/group/create')}
        />
      )}
      {data.map((entry: TSchema) => {
        const numberOfCompanies = entry?.companies?.filter(
          (company) => company,
        )?.length;

        const numberOfBranches = entry?.companies?.flatMap(
          (company) => company?.branches ?? [],
        )?.length;

        return (
          <tr
            key={entry?.id}
            className={twMerge('', !entry?.active && 'tr-deactivated')}
          >
            {!isModerator && (
              <TableCell
                tdClassName="sticky-select-column"
                content={
                  <Checkbox
                    checked={Boolean(
                      selectedTableData?.find((item) => item?.id === entry?.id),
                    )}
                    onChange={() => onSelectedItem(entry?.id)}
                  />
                }
              />
            )}
            <TableCell content={entry?.name} />
            <TableCell
              content={
                <CellContentWithRedirect
                  text={numberOfCompanies}
                  showActionButton={!isModerator}
                  disabled={!numberOfCompanies}
                  onClick={() => navigate(`/companies/group/${entry?.id}`)}
                />
              }
            />
            <TableCell
              content={
                <CellContentWithRedirect
                  text={numberOfBranches}
                  showActionButton={!isModerator}
                  disabled={!numberOfBranches}
                  onClick={() => navigate(`/branches/group/${entry?.id}`)}
                />
              }
            />
            <TableCell
              content={
                <div className="flex flex-wrap justify-center gap-2">
                  {entry?.isDistributor && (
                    <StatusTag
                      text={t('common:distributor')}
                      className="min-w-[90px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  )}
                  {entry?.isBranch && (
                    <StatusTag
                      text={t('common:branch')}
                      className="min-w-[90px] bg-secondary-light border-gray-DBDFE9 text-gray-78829D text-xs dark-mode:border-gray-300 dark-mode:bg-gray-100 dark-mode:text-gray-700 dark-mode:font-normal"
                    />
                  )}
                </div>
              }
            />
            <TableCell
              content={
                <>
                  {AddressDisplay({
                    addressLine: entry?.addressLine1,
                    postalCode: entry?.postalCode,
                    city: entry?.city,
                    country: entry?.country,
                  })}
                </>
              }
            />
            <TableCell
              tdClassName="text-center"
              content={
                <StatusTag
                  text={`${
                    entry?.isWhiteLabel
                      ? t('common:active')
                      : t('common:inactive')
                  }`}
                  className={twMerge(
                    'pt-[6px] pb-[5px] text-[10.1px] font-semibold [word-spacing:1.9px] leading-snug uppercase',
                    entry?.isWhiteLabel &&
                      'bg-green-EAFFF2 border-green-C9F4D2 text-green-11BFA4',
                    !entry?.isWhiteLabel &&
                      'bg-red-FFEEF4 border-red-FFCAD6 text-red-FF3680',
                  )}
                />
              }
            />
            <TableCell
              tdClassName="sticky-action-column"
              content={
                <TableMenu>
                  <SubMenuItem
                    text={t('common:edit')}
                    leftIconClassName="ki-notepad-edit !text-[16px]"
                    to={
                      !isAdmin && isModerator
                        ? `/group/edit/${entry?.id}?tab=${EditGroupTab.REPORT}`
                        : `/group/edit/${entry?.id}`
                    }
                  />
                  {!isModerator && (
                    <SubMenuItem
                      text={t('common:deactivate')}
                      disabled={!entry?.active || pendingDeactivate}
                      leftIconClassName="ki-trash !text-[19px]"
                      textClassName="line-clamp-1"
                      onClick={() => onDeactivate([entry])}
                    />
                  )}
                </TableMenu>
              }
            />
          </tr>
        );
      })}
    </>
  );
};
